import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOrders } from "../Redux/Reducers/ProductSlice";
import DefaultAvatar from '../otherImages/default.png';

const ThankYouPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { orders: ordersData, loading, error } = useSelector((state) => state.products || {});

  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  const orders = ordersData?.data?.orders || ordersData || [];
  const parsedId = parseInt(id, 10);
  const order = orders.find(o => o.id === parsedId || o.order_id === parsedId || o.order_number === id);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCountryName = (countryCode) => {
    const countries = {
      US: "United States",
      CA: "Canada",
      IN: "India",
      UK: "United Kingdom",
      AU: "Australia",
    };
    return countries[countryCode] || countryCode;
  };

  const getPaymentMethod = (method) => {
    const methods = {
      card: "Credit Card",
      cash: "Cash on Delivery",
      paypal: "PayPal",
    };
    return methods[method] || method;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "$0.00";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const getItemImages = (item) => {
    if (Array.isArray(item.images)) return item.images;
    if (Array.isArray(item.product?.images)) return item.product.images;
    if (Array.isArray(item.product_variant?.images)) return item.product_variant.images;
    if (Array.isArray(item.product_variant?.product?.images)) return item.product_variant.product.images;
    return [];
  };

  const getProductImage = (item) => {
    const images = getItemImages(item);
    const itemColorCode = normalizeText(item.color_code || item.product?.color_code || item.product_variant?.color_code);
    const itemColor = normalizeText(item.color || item.product?.color || item.product_variant?.color);

    const matchedImage = images.find((image) => {
      const imageColorCode = normalizeText(image.color_code || image.colorCode);
      const imageColor = normalizeText(image.color || image.name || image.color_name);
      return (itemColorCode && imageColorCode === itemColorCode) || (itemColor && imageColor === itemColor);
    });

    if (matchedImage) return matchedImage.image_url || matchedImage.url;
    if (item.image_url) return item.image_url;
    if (images.length > 0) return images[0]?.image_url || images[0]?.url || DefaultAvatar;
    return DefaultAvatar;
  };

  const getItemColor = (item) => item.color || item.product?.color || item.product_variant?.color || "";

  const getItemColorCode = (item) => item.color_code || item.product?.color_code || item.product_variant?.color_code || "";

  const getBuyTypeLabel = (item) => {
    const buyType = item.buy_type || item.type || item.product?.buy_type || item.product_variant?.buy_type;
    if (buyType === "rent") return "Rent";
    if (buyType === "buy") return "Buy";
    return "Rent";
  };

  const isRentItem = (item) => {
    const buyType = item.buy_type || item.type || item.product?.buy_type || item.product_variant?.buy_type;
    return buyType === "rent";
  };

  const getEventDate = (item) => {
    const eventDate =
      item.event_date ||
      item.eventDate ||
      item.rental_event_date ||
      item.product?.event_date ||
      item.product_variant?.event_date;

    return eventDate ? formatDate(eventDate) : "";
  };

  const getEventName = (item) => {
    return (
      item.event_name ||
      item.eventName ||
      item.rental_event_name ||
      item.product?.event_name ||
      item.product_variant?.event_name ||
      ""
    );
  };

  const getSkuNumber = (item) => {
    if (item.sku) return item.sku;
    if (item.product_variant?.sku) return item.product_variant.sku;
    if (item.product?.sku) return item.product.sku;
    return "-";
  };

  const getStyleLabel = (item) => {
    const buyType = item.buy_type || item.type || item.product?.buy_type || item.product_variant?.buy_type;
    if (buyType === "buy") return "Buy Style";
    if (buyType === "rent") return "Rent Style";
    return "Style";
  };

  const getStyle = (item) => {
    const buyType = item.buy_type || item.type || item.product?.buy_type || item.product_variant?.buy_type;
    if (buyType === "buy") {
      return item.buy_style || item.product_buy_style || item.product?.buy_style || item.product?.product_buy_style || item.product_variant?.buy_style || item.product_variant?.product?.buy_style || item.product_variant?.product?.product_buy_style || "-";
    } else if (buyType === "rent") {
      return item.rent_style || item.product_rent_style || item.product?.rent_style || item.product?.product_rent_style || item.product_variant?.rent_style || item.product_variant?.product?.rent_style || item.product_variant?.product?.product_rent_style || "-";
    }
    return "-";
  };

  const normalizeSizeValue = (value) => {
    if (!value && value !== 0) return null;
    return String(value).trim();
  };

  const parseSuitSizes = (group) => {
    const item = group.items?.[0] || group;
    const variants = item?.variants || [];
    const sizes = { coat: [], pant: [] };

    variants.forEach((variant) => {
      const rawSize = normalizeSizeValue(variant.size || variant.size_measurement || variant.size_type || variant.size || variant.product?.size || variant.product_variant?.size);
      if (!rawSize) return;
      const lower = rawSize.toLowerCase();

      if (lower.includes('coat')) {
        sizes.coat.push(rawSize.replace(/coat\s*[-:]?\s*/i, '').trim());
      } else if (lower.includes('pant')) {
        sizes.pant.push(rawSize.replace(/pants?\s*[-:]?\s*/i, '').trim());
      } else {
        if (rawSize.includes('-')) {
          const [first] = rawSize.split('-');
          if (first.toLowerCase().includes('coat')) {
            sizes.coat.push(rawSize.replace(/coat\s*[-:]?\s*/i, '').trim());
          } else if (first.toLowerCase().includes('pant')) {
            sizes.pant.push(rawSize.replace(/pants?\s*[-:]?\s*/i, '').trim());
          } else {
            sizes.coat.push(rawSize);
          }
        } else {
          sizes.coat.push(rawSize);
        }
      }
    });

    return {
      coat: sizes.coat.length ? sizes.coat.join(', ') : null,
      pant: sizes.pant.length ? sizes.pant.join(', ') : null,
    };
  };

  const getItemSizeLabel = (item) => {
    const rawSize = normalizeSizeValue(
      item.size ||
      item.size_type ||
      item.size_measurement ||
      item.product?.size ||
      item.product?.size_measurement ||
      item.product_variant?.size ||
      item.product_variant?.size_measurement ||
      item.variant?.size ||
      item.variant?.size_measurement
    );

    if (rawSize) return rawSize;

    const variant = item.variants?.[0] || item.product_variant;
    const variantSize = normalizeSizeValue(variant?.size || variant?.size_measurement || variant?.size_type);
    return variantSize;
  };

  const groupedItems = () => {
    if (!order?.items) return [];

    const groups = {};
    const standaloneItems = [];

    order.items.forEach((item) => {
      if (item.group_uuid) {
        if (!groups[item.group_uuid]) {
          groups[item.group_uuid] = {
            group_uuid: item.group_uuid,
            product_name: item.product_name || "Suit",
            items: [],
            quantity: item.variants?.[0]?.quantity || item.quantity || 1,
            total_price: 0,
          };
        }
        groups[item.group_uuid].items.push(item);
        groups[item.group_uuid].total_price += item.total_price || item.unit_price * item.quantity || 0;
      } else {
        standaloneItems.push(item);
      }
    });

    return [...Object.values(groups), ...standaloneItems];
  };

  const displayItems = groupedItems();
  const isSuitGroup = (item) => item.items && Array.isArray(item.items);

  const subtotal = order?.subtotal || order?.summary?.subtotal || 0;
  const total = order?.total_amount || order?.summary?.total_amount || subtotal;

  if (loading) {
    return (
      <div className="thankyou-page">
        <div className="thankyou-container container">
          <div className="order-details-card" style={{ textAlign: "center", padding: "50px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="thankyou-page">
        <div className="thankyou-container container">
          <div className="order-details-card" style={{ textAlign: "center", padding: "50px" }}>
            <div className="error-icon" style={{ fontSize: "48px", color: "#dc3545" }}>!</div>
            <h2>Order Not Found</h2>
            <p>We couldn't find your order information.</p>
            <button className="designBtn2 mt-3" onClick={() => navigate("/shop")}>
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="thankyou-page">
      <div className="thankyou-container container">
        <div className="order-details-card" data-aos="fade-up" data-aos-delay="200">
          <div className="card-header">
            <div className="header-left">
              <span className="order-id">Order #{order.order_number}</span>
              <span className="payment-method">{getPaymentMethod(order.payment_method)}</span>
            </div>
          </div>
          <div className="details-grid">
            <div className="items-section">
              <h3 className="section-title">Order Items ({displayItems.length})</h3>
              <div className="items-list">
                {displayItems.map((item, index) => {
                  if (isSuitGroup(item)) {
                    return (
                      <div className="list-item with-image" key={item.group_uuid || index}>
                        <div className="item-image">
                          <img src={getProductImage(item.items[0])} alt={item.product_name} onError={(e) => { e.target.src = "/Images/suit1.png"; }} />
                        </div>
                        <div className="item-info">
                          <div className="item-name-wrapper">
                            <span className="item-name">{item.product_name}</span>
                            <span className="item-badge">{getBuyTypeLabel(item.items[0])}</span>
                          </div>
                          <div className="item-meta">
                            <span className="item-quantity"><strong>Qty:</strong> {item.quantity}</span>
                            {getItemColor(item.items[0]) && (
                              <span className="item-color" style={{ marginLeft: '12px', color: '#000' }}>
                                <strong>Color:</strong>{" "}
                                {getItemColorCode(item.items[0]) && (
                                  <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: getItemColorCode(item.items[0]), border: '1px solid #ddd', verticalAlign: 'middle', marginRight: '4px' }} />
                                )}
                                {getItemColor(item.items[0])}
                              </span>
                            )}
                          </div>
                          <div className="item-meta">
                            {getStyle(item.items[0]) !== "-" && (
                                <span className="item-style" style={{color: '#000' }}><strong>{getStyleLabel(item.items[0])}:</strong> {getStyle(item.items[0])}</span>
                              )}
                          </div>
                          <div className="item-meta">
                            {(() => {
                              const sizes = parseSuitSizes(item);
                              return (
                                <>
                                  {sizes.coat && <span className="item-size" style={{ marginRight: '12px' }}><strong>Coat:</strong> {sizes.coat}</span>}
                                  {sizes.pant && <span className="item-size"><strong>Pant:</strong> {sizes.pant}</span>}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div style={{ marginLeft: "auto", textAlign: "right" }}>
                          {isRentItem(item.items[0]) && getEventDate(item.items[0]) && (
                            <>
                              {getEventName(item.items[0]) && (
                                <div className="item-meta" style={{ color: '#000' }}><strong>Event Name:</strong> {getEventName(item.items[0])}</div>
                              )}
                              <div className="item-meta" style={{ color: '#000' }}><strong>Event Date:</strong> {getEventDate(item.items[0])}</div>
                            </>
                          )}
                          <div className="item-price">{formatPrice(item.total_price)}</div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="list-item with-image" key={item.id || index}>
                        <div className="item-image">
                          <img src={getProductImage(item)} alt={item.product_name} onError={(e) => { e.target.src = "/Images/suit1.png"; }} />
                        </div>
                        <div className="item-info">
                          <div className="item-name-wrapper">
                            <span className="item-name">{item.product_name}</span>
                            <span className="item-badge">{getBuyTypeLabel(item)}</span>
                          </div>
                          <div className="item-meta">
                            <span className="item-quantity"><strong>Qty:</strong> {item.quantity || 1}</span>
                            {getItemColor(item) && (
                              <span className="item-color" style={{ marginLeft: '12px', color: '#000' }}>
                                <strong>Color:</strong>{" "}
                                {getItemColorCode(item) && (
                                  <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: getItemColorCode(item), border: '1px solid #ddd', verticalAlign: 'middle', marginRight: '4px' }} />
                                )}
                                {getItemColor(item)}
                              </span>
                            )}
                          </div>
                          <div className="item-meta">
                            {getStyle(item) !== "-" && (
                                <span className="item-style" style={{color: '#000' }}><strong>{getStyleLabel(item)}:</strong> {getStyle(item)}</span>
                              )}
                          </div>
                          {getItemSizeLabel(item) && (
                            <div className="item-meta">
                              <span className="item-size" style={{ marginTop: '4px', color: '#000' }}><strong>Size:</strong> {getItemSizeLabel(item)}</span>
                            </div>
                          )}
                        </div>
                        <div style={{ marginLeft: "auto", textAlign: "right" }}>
                          {isRentItem(item) && getEventDate(item) && (
                            <>
                              {getEventName(item) && (
                                <div className="item-meta" style={{ color: '#000' }}><strong>Event Name:</strong> {getEventName(item)}</div>
                              )}
                              <div className="item-meta" style={{ color: '#000' }}><strong>Event Date:</strong> {getEventDate(item)}</div>
                            </>
                          )}
                          <div className="item-price">{formatPrice(item.unit_price)}</div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>

              <div className="price-breakdown">
                <div className="breakdown-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="breakdown-row shipping">
                  <span>Shipping</span>
                  <span className="free">FREE</span>
                </div>
                <div className="breakdown-row total">
                  <span>Total</span>
                  <span className="total-amount">{formatPrice(total)}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              {order.shipping_address && (
                <div className="info-block">
                  <h3 className="section-title">Shipping Address</h3>
                  <div className="address-block">
                    <p><strong>Name:</strong> {order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                    <p><strong>Address:</strong> {order.shipping_address.address}</p>
                    <p><strong>City:</strong> {order.shipping_address.city}</p>
                    <p><strong>State:</strong> {order.shipping_address.state}</p>
                    <p><strong>Zip Code:</strong> {order.shipping_address.zip_code}</p>
                    <p><strong>Country:</strong> {getCountryName(order.shipping_address.country)}</p>
                  </div>
                </div>
              )}

              {order.shipping_address && (
                <div className="info-block">
                  <h3 className="section-title">Contact Information</h3>
                  <div className="contact-block">
                    <p><strong>Email:</strong> <a href={`mailto:${order.shipping_address.email}`}>{order.shipping_address.email}</a></p>
                    <p><strong>Phone:</strong> <a href={`tel:${order.shipping_address.phone}`}>{order.shipping_address.phone}</a></p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
